// React adapter over @synvya/capabilities getRestaurantProfile.
//
// The fetch/parse logic lives in the shared capabilities library (used by
// account/, the CLI, and Admin). This hook is only the React useState/useEffect
// glue that can't live in a framework-agnostic package — it exposes the
// restaurant's kind:0 avatar + name to a component. Used by the accept-invite
// card; the same getRestaurantProfile call is made inline by ProfileEditorPage.

import { useEffect, useState } from 'react'
import { getDefaultRelays } from '@/lib/baseUrl'

export interface RestaurantProfileResult {
  /** kind:0 picture URL, or null if none / not loaded. */
  picture: string | null
  /** displayName ?? name from the kind:0 profile, or null. */
  name: string | null
  loading: boolean
}

export function useRestaurantProfile(pubkey: string | null): RestaurantProfileResult {
  const [state, setState] = useState<RestaurantProfileResult>({
    picture: null,
    name: null,
    loading: Boolean(pubkey),
  })

  useEffect(() => {
    if (!pubkey) {
      setState({ picture: null, name: null, loading: false })
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true }))

    import('@synvya/capabilities')
      .then(({ getRestaurantProfile }) =>
        getRestaurantProfile({ pubkey, relays: getDefaultRelays() }),
      )
      .then((res) => {
        if (cancelled) return
        const p = res.profile
        setState({
          picture: p?.picture ?? null,
          name: p?.displayName ?? p?.name ?? null,
          loading: false,
        })
      })
      .catch(() => {
        if (!cancelled) setState({ picture: null, name: null, loading: false })
      })

    return () => {
      cancelled = true
    }
  }, [pubkey])

  return state
}
